import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { PageHead } from "@/components/site/PageHead";
import { Stars } from "@/components/site/Stars";

const ItemReview = ({ item, auth }) => {
  const [form, setForm] = useState({ name: "", rating: 0, comment: "" });
  const [done, setDone] = useState(item.reviewed);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return toast.error("Seleziona da 1 a 5 stelle");
    setBusy(true);
    try {
      await api.post("/reviews", { ...auth, product_id: item.product_id, ...form });
      setDone(true);
      toast.success("Grazie! La recensione sarà visibile dopo l'approvazione.");
    } catch (er) {
      toast.error(formatErr(er));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#18181C] border border-zinc-800 p-6" data-testid={`review-item-${item.product_id}`}>
      <h3 className="font-display italic uppercase font-extrabold text-2xl">{item.name}</h3>
      {done ? (
        <p className="flex items-center gap-2 text-sm text-zinc-400 mt-4" data-testid={`review-done-${item.product_id}`}><CheckCircle2 size={18} className="text-red-500" /> Recensione inviata — in attesa di approvazione.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4 mt-5">
          <Stars value={form.rating} size={28} onChange={(rating) => setForm({ ...form, rating })} testid={`review-stars-${item.product_id}`} />
          <input className="tms-input" placeholder="Il tuo nome" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`review-name-${item.product_id}`} />
          <textarea className="tms-input min-h-[110px]" placeholder="Com'è andata?" required minLength={3} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid={`review-comment-${item.product_id}`} />
          <button className="btn-red" disabled={busy} data-testid={`review-submit-${item.product_id}`}>{busy ? "Invio…" : "Invia recensione"}</button>
        </form>
      )}
    </div>
  );
};

export default function ReviewPage() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [creds, setCreds] = useState({ order_code: "", email: "" });
  const [order, setOrder] = useState(null);
  const [auth, setAuth] = useState(null);
  const [busy, setBusy] = useState(false);

  const verify = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post("/reviews/verify", payload);
      setOrder(data);
      setAuth(payload);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (sid) verify({ session_id: sid }); }, [sid]);

  return (
    <div data-testid="review-page">
      <PageHead eyebrow="Recensioni verificate" title="Lascia una" script="recensione" sub="Solo chi ha acquistato può recensire. Inserisci il codice ordine e l'email usata per il pagamento." />
      <section className="max-w-3xl mx-auto px-5 lg:px-8 py-14">
        {!order ? (
          <form onSubmit={(e) => { e.preventDefault(); verify(creds); }} className="bg-[#121215] border border-zinc-800 p-8 space-y-5" data-testid="review-verify-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Codice ordine</label>
              <input className="tms-input mt-2 uppercase" placeholder="TMS-XXXXXX" required value={creds.order_code} onChange={(e) => setCreds({ ...creds, order_code: e.target.value })} data-testid="review-order-code-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Email del pagamento</label>
              <input type="email" className="tms-input mt-2" placeholder="nome@email.it" required value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} data-testid="review-email-input" />
            </div>
            <button className="btn-red w-full" disabled={busy} data-testid="review-verify-button">{busy ? "Verifica…" : "Verifica acquisto"}</button>
          </form>
        ) : (
          <div className="space-y-6" data-testid="review-items">
            <p className="text-sm text-zinc-400">Ordine <span className="text-white font-bold" data-testid="review-order-code">{order.order_code}</span> — recensisci gli articoli acquistati:</p>
            {order.items.map((i) => <ItemReview key={i.product_id} item={i} auth={auth} />)}
          </div>
        )}
      </section>
    </div>
  );
}
